/**
 * UniQ (survey) Web API client。Cognito ID トークンを Authorization に付けて survey API を叩く。
 * tenantId は root loader が設定し、全リクエストの query に自動付与する (dev-lab/admin と同方式)。
 */
import { fetchAuthSession } from "aws-amplify/auth";
import { handleApiError } from "@shared-ui/utils/api";
import { ENV } from "@/config/env";
import type {
  Answer,
  AnswerMode,
  Assignment,
  CreateSurveyRequest,
  Survey,
  SurveyListItem,
  SurveyListView,
  SurveyResponse,
  SurveyResults,
  SurveyStatus,
  UpdateSurveyRequest,
} from "@shared-domain/survey/contract";
import type { Customer } from "@shared-domain/lib/customer/provider";
import type { GBizCompany } from "@shared-domain/lib/gbiz/types";

export interface Rep {
  userId: string;
  name: string;
  /** 所属部署 (Unizo 部署マスタ)。未所属/部署連携なし env では undefined。 */
  departmentId?: string;
}
export interface DeptSummary {
  deptId: string;
  name: string;
}
export interface FreeCustomer {
  name: string;
  no?: string;
  addr?: string;
  contact?: string;
}

let activeTenantId: string | undefined;
export function setActiveTenantId(id: string | undefined): void {
  activeTenantId = id;
}

function withTenant(url: string): string {
  if (!activeTenantId) return url;
  const u = new URL(url);
  u.searchParams.set("tenantId", activeTenantId);
  return u.toString();
}

async function authHeader(): Promise<HeadersInit> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function currentUserName(): Promise<string | undefined> {
  const session = await fetchAuthSession();
  const p = session.tokens?.idToken?.payload;
  const name = p?.name;
  const email = p?.email;
  if (typeof name === "string" && name) return name;
  if (typeof email === "string" && email) return email;
  return undefined;
}

async function fetchWithAuth(url: string, init?: RequestInit): Promise<Response> {
  const auth = await authHeader();
  return fetch(url, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...auth,
      ...(init?.headers ?? {}),
    },
  });
}

async function requestUrl<T>(url: string, init?: RequestInit): Promise<T> {
  let res = await fetchWithAuth(url, init);
  if (res.status === 401) {
    try {
      await fetchAuthSession({ forceRefresh: true });
    } catch {
      /* refresh token 失効 → 下で handleApiError がポータルへ */
    }
    res = await fetchWithAuth(url, init);
  }
  if (!res.ok) {
    if (res.status === 401) await handleApiError(res);
    const text = await res.text().catch(() => "");
    throw new Error(`API ${res.status}: ${text || res.statusText}`);
  }
  return (await res.json()) as T;
}

function request<T>(path: string, init?: RequestInit): Promise<T> {
  return requestUrl(withTenant(`${ENV.PRODUCT_API_ENDPOINT}${path}`), init);
}

const enc = encodeURIComponent;

export const surveyApi = {
  // ── read ───────────────────────────────────────────────
  list: (view: SurveyListView): Promise<{ surveys: SurveyListItem[] }> =>
    request(`/surveys?view=${view}`),
  get: (id: string): Promise<{ survey: Survey }> =>
    request(`/surveys/${enc(id)}`),
  results: (id: string): Promise<{ results: SurveyResults }> =>
    request(`/surveys/${enc(id)}/results`),
  responses: (id: string): Promise<{ responses: SurveyResponse[] }> =>
    request(`/surveys/${enc(id)}/responses`),
  myResponses: (id: string): Promise<{ responses: SurveyResponse[] }> =>
    request(`/surveys/${enc(id)}/responses/me`),
  myAssignments: (id: string): Promise<{ assignments: Assignment[] }> =>
    request(`/surveys/${enc(id)}/my-assignments`),
  assignments: (id: string): Promise<{ assignments: Assignment[] }> =>
    request(`/surveys/${enc(id)}/assignments`),
  reps: (): Promise<{ reps: Rep[]; departments?: DeptSummary[] }> => request("/surveys/reps"),
  customers: (id: string, q?: string): Promise<{ customers: Customer[] }> =>
    request(`/surveys/${enc(id)}/customers${q ? `?q=${enc(q)}` : ""}`),
  // 対象顧客名の入力補助: 顧客マスタ + gBizINFO 統合検索。
  companySearch: (name: string): Promise<{ customers: Customer[]; gbiz: GBizCompany[] }> =>
    request(`/surveys/company-search?name=${enc(name)}`),
  gbizDetail: (corporateNumber: string): Promise<{ company: GBizCompany | null }> =>
    request(`/surveys/company/gbiz-detail?corporateNumber=${enc(corporateNumber)}`),

  // ── write (本部) ───────────────────────────────────────
  create: (body: CreateSurveyRequest): Promise<{ survey: Survey }> =>
    request("/surveys", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: UpdateSurveyRequest): Promise<{ survey: Survey }> =>
    request(`/surveys/${enc(id)}`, { method: "PUT", body: JSON.stringify(body) }),
  remove: (id: string): Promise<{ deleted: boolean }> =>
    request(`/surveys/${enc(id)}`, { method: "DELETE" }),
  setStatus: (id: string, status: SurveyStatus): Promise<{ status: string }> =>
    request(`/surveys/${enc(id)}/status`, {
      method: "POST",
      body: JSON.stringify({ status }),
    }),
  assign: (
    id: string,
    assignments: { repUserId: string; customerId: string }[],
  ): Promise<{ assignments: Assignment[] }> =>
    request(`/surveys/${enc(id)}/assignments`, {
      method: "POST",
      body: JSON.stringify({ assignments }),
    }),
  unassign: (id: string, repUserId: string, customerId: string): Promise<{ deleted: boolean }> =>
    request(
      `/surveys/${enc(id)}/assignments?repUserId=${enc(repUserId)}&customerId=${enc(customerId)}`,
      { method: "DELETE" },
    ),

  // ── voice (音声モード) ─────────────────────────────────
  realtimeSession: (
    surveyId: string,
    mode: "freerec" | "dialog",
  ): Promise<{ clientSecret: { value: string; expiresAt?: number }; model: string }> =>
    request(`/openai/realtime/session?surveyId=${enc(surveyId)}&mode=${mode}`),
  extract: (surveyId: string, transcript: string): Promise<{ answers: Answer[] }> =>
    request(`/surveys/${enc(surveyId)}/extract`, {
      method: "POST",
      body: JSON.stringify({ transcript }),
    }),
  // 自由モード: 録音音声(base64)を Whisper で文字起こし。
  transcribe: (
    surveyId: string,
    audioBase64: string,
    mimeType: string,
  ): Promise<{ text: string }> =>
    request(`/surveys/${enc(surveyId)}/transcribe`, {
      method: "POST",
      body: JSON.stringify({ audioBase64, mimeType }),
    }),

  // ── respond (営業担当) ─────────────────────────────────
  respond: (
    id: string,
    body: {
      customerId: string;
      answers: Answer[];
      answerMode?: AnswerMode;
      freeCustomer?: FreeCustomer;
    },
  ): Promise<{ submitted: boolean }> =>
    request(`/surveys/${enc(id)}/responses`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
};
